import { useTranslation } from 'react-i18next';

import { Messages } from '@ifs-gcp/components';
import type { AuditCreation, IAppContextProvider, ITemplateNode, Standards } from '@ifs-gcp/core';
import {
  isQuestionNode,
  isQuestionTextNode,
  isWidgetAuditComposeNode,
  isWidgetModuleLogoGalleryNode,
} from '@ifs-gcp/core/helpers';
import type { StepContentChild } from '@ifs-gcp/core/models';

import { ModuleLogos } from '@/components/module-logos';
import { QuestionText } from '@/components/question/question-text';

import type { StepCompleteData, StepStatus } from './dashboard-modal';
import styles from './dashboard-template-element.module.scss';
import { AuditComposer } from './widgets/audit-compose/audit-compose';
import { AuditCreationQuestion } from './widgets/question';

type TemplateElementProps = {
  moduleIdsInUse: string[];
  domain: AuditCreation;
  element: ITemplateNode<StepContentChild['children'][number]>;
  status: StepStatus;
  standards: Standards;
  userProvider: IAppContextProvider;
  showDisableWarning?: boolean;
  onComplete: (data: StepCompleteData) => void;
};

export const TemplateElement = ({
  moduleIdsInUse,
  element,
  domain,
  status,
  standards,
  userProvider,
  showDisableWarning,
  onComplete,
}: TemplateElementProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  if (isQuestionTextNode(element)) {
    return (
      <QuestionText element={element} conditions={domain.conditionHandler} language={language} />
    );
  }

  if (isQuestionNode(element)) {
    return (
      <AuditCreationQuestion element={element} domain={domain} moduleIdsInUse={moduleIdsInUse} />
    );
  }

  if (isWidgetAuditComposeNode(element)) {
    return (
      <AuditComposer
        element={element}
        domain={domain}
        status={status}
        standards={standards}
        userProvider={userProvider}
        onComplete={onComplete}
      />
    );
  }

  if (isWidgetModuleLogoGalleryNode(element)) {
    return (
      <>
        <ModuleLogos element={element} />

        {showDisableWarning && (
          <div className={styles.warningContainer}>
            <Messages
              messages={[t('dashboard.start_modal.module_selector_warning')]}
              type="warning-box"
              headerText={t('common.warning')}
              variant="plain"
            />
          </div>
        )}
      </>
    );
  }

  return null;
};
